import { formatDate } from "../../Helper/generalhelper";
import { valuesAreEquivalent } from "./scoopChangedFields";

const INTERNAL_LOG_STATUS = "internal_res";

const HIDDEN_INTERNAL_KEYS = new Set([
  "created_by",
  "dtCreationDate",
  "dtUpdatedDate",
  "vPassword",
  "org_pass",
  "S3vProfilePic",
  "image",
  "vSignUpload",
  "activation_token",
  "iUserId",
  "vUserName",
  "vResourcePosition",
  "tabPermission",
  "menu_access",
]);

// Labels from AddUpdateResource Form.js. Unknown keys stay hidden.
const INTERNAL_FIELD_LABELS = {
  eUserStatus: "Resource Status",
  vFirstName: "First Name",
  vLastName: "Last Name",
  iGender: "Gender",
  dtBirthDate: "Date of Birth",
  vEmailAddress: "Email Address",
  iMobile: "Mobile",
  vSkypeName: "Teams/Slack Username",
  iResourceNumber: "Profile Number",
};

const GENDER_LABELS = {
  1: "Male",
  2: "Female",
  3: "Other",
};

const PREVIEW_LIMIT = 4;

export function isInternalResourceLog(logStatus) {
  return String(logStatus || "").trim().toLowerCase() === INTERNAL_LOG_STATUS;
}

function parseDetails(details) {
  if (!details) return null;
  if (typeof details === "object") return details;
  try {
    return JSON.parse(details);
  } catch (e) {
    return null;
  }
}

function parseChangedFields(details) {
  const parsed = parseDetails(details);
  if (!parsed) return null;

  let changedFields = parsed.changedFields;
  if (typeof changedFields === "string") {
    try {
      changedFields = JSON.parse(changedFields);
    } catch (e) {
      return null;
    }
  }

  if (!changedFields || typeof changedFields !== "object" || Array.isArray(changedFields)) {
    return null;
  }

  return changedFields;
}

function looksLikeJson(value) {
  if (typeof value !== "string") return false;
  const trimmed = value.trim();
  return trimmed.startsWith("{") || trimmed.startsWith("[");
}

function parseMaybeJson(value) {
  if (value && typeof value === "object") return value;
  if (!looksLikeJson(value)) return value;
  try {
    return JSON.parse(value.trim());
  } catch (e) {
    return value;
  }
}

function isEmptyValue(value) {
  if (value === null || value === undefined) return true;
  if (typeof value === "string") {
    const trimmed = value.trim();
    return trimmed === "" || trimmed.toLowerCase() === "null" || trimmed === "[]" || trimmed === "{}";
  }
  return false;
}

function isBlankDate(value) {
  if (isEmptyValue(value)) return true;
  const raw = String(value).trim().toLowerCase();
  return raw === "invalid date" || raw.startsWith("0000-00-00");
}

function isDiffShape(change) {
  if (!change || typeof change !== "object" || Array.isArray(change)) return false;
  return (
    Object.prototype.hasOwnProperty.call(change, "old") ||
    Object.prototype.hasOwnProperty.call(change, "new")
  );
}

function lookupName(value, nameMap) {
  if (isEmptyValue(value)) return "";
  const id = String(value).trim();
  return nameMap?.[id] || id;
}

function formatDateField(value) {
  if (isBlankDate(value)) return "";
  return formatDate(String(value).trim(), { includeTime: false });
}

function formatGender(value) {
  if (isEmptyValue(value)) return "";
  return GENDER_LABELS[String(value).trim()] || String(value);
}

function formatMobile(value) {
  const parsed = parseMaybeJson(value);
  if (parsed && typeof parsed === "object" && !Array.isArray(parsed)) {
    return String(parsed.mobileNumber || "").trim();
  }
  return isEmptyValue(value) ? "" : String(value);
}

function formatFieldValue(fieldKey, value, lookups) {
  if (fieldKey === "eUserStatus") return lookupName(value, lookups.statusNames);
  if (fieldKey === "iGender") return formatGender(value);
  if (fieldKey === "dtBirthDate") return formatDateField(value);
  if (fieldKey === "iMobile") return formatMobile(value);
  if (isEmptyValue(value)) return "";
  if (typeof value === "object") return JSON.stringify(value);
  return String(value);
}

export function getInternalResourceChangedFields(details, lookups = {}) {
  const changedFields = parseChangedFields(details);
  if (!changedFields) return [];

  return Object.keys(changedFields)
    .filter(key => INTERNAL_FIELD_LABELS[key] && !HIDDEN_INTERNAL_KEYS.has(key))
    .filter(key => {
      const change = changedFields[key];
      if (!isDiffShape(change)) return false;
      if (valuesAreEquivalent(change.old, change.new, key)) return false;
      const oldValue = formatFieldValue(key, change.old, lookups);
      const newValue = formatFieldValue(key, change.new, lookups);
      return oldValue !== newValue;
    })
    .map(key => ({
      key,
      label: INTERNAL_FIELD_LABELS[key],
      old: formatFieldValue(key, changedFields[key].old, lookups),
      new: formatFieldValue(key, changedFields[key].new, lookups),
    }));
}

export function getInternalResourceChangedSummary(changes) {
  const displayable = Array.isArray(changes) ? changes : [];
  const previewLabels = displayable.slice(0, PREVIEW_LIMIT).map(change => change.label);
  const remaining = Math.max(displayable.length - PREVIEW_LIMIT, 0);

  return {
    text: previewLabels.length ? `Changed: ${previewLabels.join(", ")}` : "",
    remaining,
  };
}
